import texts from '../config/texts.js'

// 当前语言设置（将来可以通过状态管理来切换）
let currentLanguage = 'zh'

/**
 * 设置当前语言
 * @param {string} language - 语言代码 ('zh', 'ja', 'en')
 */
export const setLanguage = (language) => {
  if (texts[language]) {
    currentLanguage = language
    // 触发语言变化事件，通知所有组件更新
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }))
    }
  } else {
    console.warn(`Language '${language}' not found, falling back to 'zh'`)
    currentLanguage = 'zh'
  }
}

/**
 * 获取当前语言
 * @returns {string} 当前语言代码
 */
export const getCurrentLanguage = () => currentLanguage

/**
 * 根据路径获取文本
 * @param {string} path - 文本路径，用点分隔，如 'companyProfile.title'
 * @param {object} params - 文本参数（用于动态文本替换）
 * @returns {string} 文本内容
 */
export const getText = (path, params = {}) => {
  try {
    const keys = path.split('.')
    let value = texts[currentLanguage]
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key]
      } else {
        // 如果在当前语言中找不到，尝试使用默认语言（中文）
        console.warn(`Text not found for path '${path}' in language '${currentLanguage}', trying fallback`)
        value = texts.zh
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey]
          } else {
            console.error(`Text not found for path '${path}' even in fallback language`)
            return `[Missing: ${path}]`
          }
        }
        break
      }
    }
    
    // 如果有参数，进行文本替换
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, key) => {
        return params[key] !== undefined ? params[key] : match
      })
    }
    
    return value || `[Missing: ${path}]`
  } catch (error) {
    console.error(`Error getting text for path '${path}':`, error)
    return `[Error: ${path}]`
  }
}

/**
 * 获取公司简介相关文本的便捷方法
 */
export const getCompanyProfileText = (subPath) => {
  return getText(`companyProfile.${subPath}`)
}

/**
 * 获取联系页面相关文本的便捷方法
 */
export const getContactText = (subPath) => {
  return getText(`contact.${subPath}`)
}

/**
 * 获取公共文本的便捷方法
 */
export const getCommonText = (subPath) => {
  return getText(`common.${subPath}`)
}

/**
 * 获取页头相关文本的便捷方法
 */
export const getHeaderText = (subPath) => {
  return getText(`header.${subPath}`)
}

/**
 * 获取导航菜单相关文本的便捷方法
 */
export const getNavigationText = (subPath) => {
  return getText(`header.navigation.${subPath}`)
}

/**
 * 获取页脚相关文本的便捷方法
 */
export const getFooterText = (subPath) => {
  return getText(`footer.${subPath}`)
}

/**
 * 获取首页相关文本的便捷方法
 */
export const getHomepageText = (subPath) => {
  return getText(`homepage.${subPath}`)
}

/**
 * 获取顶部导航相关文本的便捷方法
 */
export const getTopHeaderText = (subPath) => {
  return getText(`topHeader.${subPath}`)
}

/**
 * 获取公司信息相关文本的便捷方法
 */
export const getCompanyText = (subPath) => {
  return getText(`company.${subPath}`)
}

/**
 * 获取包车预订相关文本的便捷方法
 */
export const getCarbookingText = (subPath) => {
  return getText(`carbooking.${subPath}`)
}

/**
 * 获取酒店预订相关文本的便捷方法
 */
export const getHotelbookingText = (subPath) => {
  return getText(`hotelbooking.${subPath}`)
}

/**
 * 获取常见问题相关文本的便捷方法
 */
export const getQuestionText = (subPath) => {
  return getText(`question.${subPath}`)
}

/**
 * 获取团体旅游相关文本的便捷方法
 */
export const getGrouptravelText = (subPath) => {
  return getText(`grouptravel.${subPath}`)
}

/**
 * 批量获取文本（用于组件初始化时一次性获取多个文本）
 * @param {Array<string>} paths - 文本路径数组
 * @returns {object} 包含所有文本的对象
 */
export const getTexts = (paths) => {
  const result = {}
  paths.forEach(path => {
    const keys = path.split('.')
    const lastKey = keys[keys.length - 1]
    result[lastKey] = getText(path)
  })
  return result
}

/**
 * 检查指定语言是否可用
 * @param {string} language - 语言代码
 * @returns {boolean} 是否可用
 */
export const isLanguageAvailable = (language) => {
  return texts[language] && Object.keys(texts[language]).length > 0
}

/**
 * 获取所有可用语言
 * @returns {Array<string>} 可用语言列表
 */
export const getAvailableLanguages = () => {
  return Object.keys(texts).filter(lang => 
    texts[lang] && Object.keys(texts[lang]).length > 0
  )
}

// 团体攻略相关的文本获取函数
export const getGroupTravelText = (key, params = {}) => {
  return getText(`grouptravel.${key}`, params)
}

// 默认导出主要的获取文本函数
export default getText